import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { SCORE_LABEL, SCORE_PATH } from "@/lib/serviceHubContracts";

type ClusterPath = {
  hubHref: string;
  hubLabel: string;
  proofHref?: string;
  proofLabel?: string;
  scoreSecondary?: boolean;
};

/**
 * One visible, contextual route per selected spoke. These are deliberately
 * source-level render rules: the pathway sits with the article's body rather
 * than becoming a navigation-wide CTA or a generic card farm.
 */
const CLUSTER_PATHS: Record<string, ClusterPath> = {
  "what-does-an-aeo-agency-uk-do-b2b-buyers-guide-2026": { hubHref: "/services/seo-aeo-geo/", hubLabel: "Explore AEO and GEO services" },
  "aeo-agency-what-to-hire-for-2026": { hubHref: "/services/seo-aeo-geo/", hubLabel: "Explore AEO and GEO services" },
  "aeo-agency-vs-in-house-vs-hybrid-b2b-decision-guide-2026": { hubHref: "/services/seo-aeo-geo/", hubLabel: "Explore AEO and GEO services" },
  "saas-aeo-case-study-what-it-must-show": { hubHref: "/services/seo-aeo-geo/", hubLabel: "Explore AEO and GEO services" },
  "llmo-agency-uk-what-it-costs": { hubHref: "/services/seo-aeo-geo/", hubLabel: "Explore AEO and GEO services" },
  "aeo-agency-pricing-uk-cost-roi-guide-2026": { hubHref: "/services/seo-aeo-geo/", hubLabel: "Explore AEO and GEO services", scoreSecondary: true },
  "reddit-geo-spam-black-hat-ai-search-2026": { hubHref: "/services/seo-aeo-geo/", hubLabel: "Explore AEO and GEO services" },
  "track-ai-overview-impressions-google-search-console": { hubHref: "/services/seo-aeo-geo/", hubLabel: "Explore AEO and GEO services", scoreSecondary: true },
  "abm-for-fintech-and-financial-services": { hubHref: "/services/account-based-marketing/", hubLabel: "Explore Account-Based Marketing", proofHref: "/case-studies/fintech-persona-targeting/", proofLabel: "Read the FinTech case study" },
  "abm-agency-london-what-good-looks-like": { hubHref: "/services/account-based-marketing/", hubLabel: "Explore Account-Based Marketing", proofHref: "/case-studies/b2b-saas-abm/", proofLabel: "Read the B2B SaaS ABM case study" },
  "ai-powered-abm-enterprise-b2b-2026": { hubHref: "/services/account-based-marketing/", hubLabel: "Explore Account-Based Marketing" },
  "multi-agent-ai-automate-gtm-2026": { hubHref: "/services/account-based-marketing/", hubLabel: "Explore Account-Based Marketing" },
  "integrated-marketing-communications-agency-ai-seo-aeo-london-2026": { hubHref: "/services/integrated-marketing-communications/", hubLabel: "Explore Integrated Marketing Communications", scoreSecondary: true },
};

export default function ClusterPathway({ slug }: { slug: string }) {
  const path = CLUSTER_PATHS[slug];
  if (!path) return null;

  return (
    <aside className="mt-10 rounded-xl border border-amber-500/25 bg-amber-500/5 p-5" aria-label="Related service pathway">
      <p className="text-xs font-semibold uppercase tracking-widest text-amber-600">Relevant next step</p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Choose the commercial route that matches the question in this guide. The scope is agreed from evidence, not inferred from one article.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Link href={path.hubHref} className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-black transition hover:bg-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2">
          {path.hubLabel} <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
        {path.proofHref && path.proofLabel && (
          <Link href={path.proofHref} className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:border-amber-500/50">
            {path.proofLabel}
          </Link>
        )}
        {path.scoreSecondary && (
          <Link href={SCORE_PATH} className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:border-amber-500/50">
            {SCORE_LABEL}
          </Link>
        )}
      </div>
    </aside>
  );
}
